import type { CardsProps } from '@risk-smart/themed-cloudscape-components/cards';
import Cards from '@risk-smart/themed-cloudscape-components/cards';
import Container from '@risk-smart/themed-cloudscape-components/container';
// Card titles are still Cloudscape Headers; the column header is a shared block.
import Header from '@risk-smart/themed-cloudscape-components/header';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import { Badge, Icon } from '@risksmart-app/atomic-ui';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import {
  Obligation_Type_Enum,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import _ from 'lodash';
import type { FC, MouseEvent } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  DrillCount,
  DrillDownColumnHeader,
} from 'src/blocks/drill-down-column';
import { RevealButton } from 'src/blocks/object-toolbar/parts/RevealButton';
import { Permission } from 'src/rbac/Permission';

import Link from '@/components/link';
import { useGetObligationById } from '@/hooks/queries';
import { getFriendlyId } from '@/utils/friendlyId';
import {
  addChildObligationUrl,
  addObligationUrl,
  obligationDetailsUrl,
} from '@/utils/urls';

import type { ObligationTableFields } from '../types';
import styles from './style.module.scss';

type CardItem = {
  id: string;
  title: string;
  rating: { color: string; label: string; value: number } | null;
  SequentialId: null | number | undefined;
  regulatorLabel?: string;
  /**
   * How many obligations in the next column down link to this one. `null` on a
   * leaf column, where there is nothing to drill into.
   */
  childCount: null | number;
};
interface Props {
  loading: boolean;
  type: Obligation_Type_Enum;
  /** The obligation selected in the PARENT column, whose children this shows. */
  selectedId?: string;
  /** The obligation selected in THIS column — drives the card highlight. */
  selectedItemId?: string;
  onSelectAction: (obligationId: string, type: Obligation_Type_Enum) => void;
  obligations: readonly ObligationTableFields[];
}
interface RatingsByValue {
  [key: number]: { label: string; color: string; value: number };
}

const childTypes: {
  [key in Obligation_Type_Enum]: Obligation_Type_Enum;
} = {
  [Obligation_Type_Enum.Standard]: Obligation_Type_Enum.Chapter,
  [Obligation_Type_Enum.Chapter]: Obligation_Type_Enum.Rule,
  [Obligation_Type_Enum.Rule]: Obligation_Type_Enum.Task,
  [Obligation_Type_Enum.Task]: Obligation_Type_Enum.Task,
};

const unlinkedId = 'unlinked';

const cardItemsFromData = (
  obligations: readonly ObligationTableFields[],
  type: Obligation_Type_Enum,
  title: string,
  ratings: RatingsByValue,
  selectedId: string | undefined
) => {
  const isLeaf = type === Obligation_Type_Enum.Task;
  // Nothing is selected in the column to the left, so this column is showing the
  // children of nothing.
  const awaitingParent =
    type !== Obligation_Type_Enum.Standard && _.isNil(selectedId);

  // How many children each obligation in this column has, so a card can show what
  // drilling into it will yield, plus how many of those children have no parent at
  // all (the synthetic bucket's own count). One pass over the register: every child
  // row lands in exactly one of the two tallies. Leaf columns drill nowhere, so the
  // pass is skipped entirely.
  const childCountByParentId = new Map<string, number>();
  let orphanedChildCount = 0;

  if (!isLeaf) {
    for (const o of obligations) {
      if (o.Type !== childTypes[type]) {
        continue;
      }
      if (_.isNil(o.ParentId)) {
        orphanedChildCount += 1;
      } else {
        childCountByParentId.set(
          o.ParentId,
          (childCountByParentId.get(o.ParentId) ?? 0) + 1
        );
      }
    }
  }

  const cardItems: CardItem[] = obligations
    .filter(
      (o) =>
        o.Type === type &&
        // A missing `selectedId` means nothing is selected in the parent column,
        // which must not match an obligation whose own `ParentId` is absent —
        // those belong to the synthetic unlinked bucket, not to "no parent".
        ((!_.isNil(selectedId) && o.ParentId === selectedId) ||
          o.Type === Obligation_Type_Enum.Standard ||
          (selectedId === unlinkedId && _.isNil(o.ParentId)))
    )
    .map((item) => ({
      id: item.Id,
      title: item.Title,
      SequentialId: item.SequentialId,
      rating: ratings[item.LatestAssessmentResult || 0],
      regulatorLabel: item.RegulatorLabel ?? undefined,
      childCount: isLeaf ? null : (childCountByParentId.get(item.Id) ?? 0),
    }));

  // The bucket is a sibling of this column's real rows, so it only belongs where
  // there are real rows to sit beside. On a column awaiting its parent selection
  // it would be the sole card — burying the prompt behind a bucket of orphaned
  // grandchildren. The same bucket is still reachable once a parent is chosen.
  if (!isLeaf && !awaitingParent && orphanedChildCount > 0) {
    cardItems.push({
      title,
      rating: null,
      id: unlinkedId,
      SequentialId: 0,
      childCount: orphanedChildCount,
    });
  }

  cardItems.sort((a, b) => {
    if (a.id === unlinkedId) {
      return 1;
    }
    if (b.id === unlinkedId) {
      return -1;
    }

    return (a.title ?? '').localeCompare(b.title ?? '', undefined, {
      sensitivity: 'base',
    });
  });

  return cardItems;
};

const Type: FC<Props> = ({
  type,
  onSelectAction,
  obligations,
  selectedId,
  selectedItemId,
  loading,
}) => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'obligations',
  });
  const { t: tc } = useTranslation(['common']);
  const { t: tr } = useTranslation(['ratings']);

  // The toolbar-style action is a real anchor, so route a plain left click through
  // the router — carrying the same `from` state the Cloudscape `onFollow` path
  // carries, which is what the breadcrumb reads — and leave modifier clicks to the
  // browser so open-in-new-tab still works.
  const navigate = useNavigate();
  const followInternally = (event: MouseEvent<HTMLElement>) => {
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }
    event.preventDefault();
    navigate(addHref, { state: { from: 'compliance-dashboard' } });
  };

  const cardItems = useMemo<CardItem[]>(() => {
    const ratingsByValue: RatingsByValue = [
      ...tr('performance_result_unrated'),
      ...tr('performance_result'),
    ].reduce((sorted, res) => ({ ...sorted, [res.value]: res }), {});

    return cardItemsFromData(
      obligations,
      type,
      t('orphaned_obligation_title'),
      ratingsByValue,
      selectedId
    );
  }, [obligations, selectedId, t, tr, type]);

  // Selection comes from the page's drill-down state, so a card can never stay
  // highlighted after the selection that revealed it was replaced.
  const selectedItems = useMemo(
    () => cardItems.filter((item) => item.id === selectedItemId),
    [cardItems, selectedItemId]
  );

  const isRootColumn = type === Obligation_Type_Enum.Standard;
  const awaitingParentSelection = !isRootColumn && !selectedId;

  // The synthetic unlinked bucket is a navigation affordance, not an obligation,
  // so it must not inflate the column's count.
  const resultCount = cardItems.filter((item) => item.id !== unlinkedId).length;

  // Three distinct states, not one. A column with no parent selected yet has not
  // "found no items" — it is waiting for the drill-down step to its left, and
  // saying so is what makes the cascade discoverable.
  const selectParentPrompt = (): string => {
    const prompts = t('dashboard.select_prompt');
    switch (type) {
      case Obligation_Type_Enum.Chapter:
        return prompts.chapter;
      case Obligation_Type_Enum.Rule:
        return prompts.rule;
      case Obligation_Type_Enum.Task:
        return prompts.task;
      case Obligation_Type_Enum.Standard:
        return '';
    }
  };

  const empty = awaitingParentSelection
    ? selectParentPrompt()
    : tc('noItemsFound');

  /*
   * The create control is icon-only under the toolbar, so its `aria-label` is the
   * whole accessible name. Four columns each announcing a bare "Add" gives a screen
   * reader four indistinguishable links, and the column heading that disambiguates
   * them visually is not programmatically associated with the control.
   */
  const createLabel = (): string => {
    const labels = t('dashboard.add');
    switch (type) {
      case Obligation_Type_Enum.Standard:
        return labels.standard;
      case Obligation_Type_Enum.Chapter:
        return labels.chapter;
      case Obligation_Type_Enum.Rule:
        return labels.rule;
      case Obligation_Type_Enum.Task:
        return labels.task;
    }
  };

  const onSelectionChange: CardsProps<CardItem>['onSelectionChange'] = ({
    detail,
  }) => {
    // Cloudscape types `selectedItems` as an array, and a deselect would arrive
    // here as an empty one. There is nothing to drill into in that case, and
    // dereferencing [0] would throw.
    const [selected] = detail.selectedItems;
    if (!selected) {
      return;
    }

    onSelectAction(selected.id, type);
  };

  // `selectedId` is the id of the obligation selected in the PARENT type's
  // column — the same role `parentRiskId` plays in `risk-dashboard/Tier.tsx`.
  // The synthetic "unlinked" card is a bucket, not a node, so it is not a parent.
  const hasRealParent = !!selectedId && selectedId !== unlinkedId;

  // Fetch the selected parent to get `ancestorContributors` for the permission
  // check. With a `parentObject` the gate resolves instance-level
  // (`can_insert_child`), which is what lets a user whose insert capability comes
  // from an ownership grant — rather than a type-level role grant — create a
  // child here (COI-642). Mirrors Tier.tsx.
  const shouldSkipParentFetch =
    type === Obligation_Type_Enum.Standard || !hasRealParent;
  const {
    data: parentObligationData,
    loading: isParentObligationLoading,
    error: parentObligationError,
  } = useGetObligationById({
    queryArgs: { id: selectedId ?? '' },
    shouldSkip: shouldSkipParentFetch,
  });
  // Explicitly null when there is no parent to fetch, so the gate can never be
  // handed a stale parent from a previous selection.
  const parentObligation = shouldSkipParentFetch
    ? null
    : (parentObligationData?.obligation?.[0] ?? null);

  const addHref = hasRealParent
    ? addChildObligationUrl(selectedId, type)
    : addObligationUrl(type);

  return (
    <div className={styles.type} data-testid={`obligation-type-${type}`}>
      <Container fitHeight variant={'stacked'}>
        <SpaceBetween direction={'vertical'} size={'m'}>
          <DrillDownColumnHeader
            title={t('dashboard_category_titles')[type] || ''}
            counter={
              loading || awaitingParentSelection || resultCount === 0
                ? undefined
                : `(${resultCount})`
            }
            action={
              // Render nothing while the parent resolves, rather than a
              // denied-looking gap that then pops in (Tier.tsx does the same).
              // Also hide when the parent lookup completed but returned no result
              // (empty data, not loading, not error) — the parent was deleted or
              // is not accessible.
              !shouldSkipParentFetch &&
              (isParentObligationLoading ||
                parentObligationError ||
                !parentObligation) ? undefined : (
                <Permission
                  permission={'insert:obligation'}
                  canHaveAccessAsContributor={
                    type !== Obligation_Type_Enum.Standard
                  }
                  parentObject={parentObligation}
                >
                  {/*
                    Always the compact icon action, whatever `object_toolbar` says.
                    Measured: at a 173px column the ~96px pill leaves the title 73px
                    and shreds "High-Level Standards" over four lines, while a 32px
                    icon leaves it 133px and two. A pill cannot share a drill-down
                    column's header row at any width users actually have, so the page
                    header keeps its flag-gated treatment and the columns do not.
                    The label is type-qualified, so the icon is not unnamed.
                  */}
                  <RevealButton
                    label={createLabel()}
                    // `none`, not a tooltip: the action is already revealed by
                    // hovering the column, and Cloudscape's Container clips an
                    // absolutely-positioned tooltip at its edge. The accessible name
                    // still comes from the label.
                    reveal={'none'}
                    href={addHref}
                    onClick={followInternally}
                  >
                    <Icon name={'plus'} size={'lg'} />
                  </RevealButton>
                </Permission>
              )
            }
          />
          <Cards<CardItem>
            ariaLabels={{
              itemSelectionLabel: (e, n) =>
                t('dashboard.select', { title: n.title }),
              selectionGroupLabel: t('dashboard.item_selection'),
            }}
            entireCardClickable={true}
            cardDefinition={{
              sections: [
                {
                  id: 'title',
                  content: (item) => (
                    <div
                      className={
                        'flex w-full items-center justify-between gap-2'
                      }
                    >
                      <div className={'inline-block'}>
                        <Link
                          variant={'secondary'}
                          href={
                            item.id !== unlinkedId
                              ? obligationDetailsUrl(item.id)
                              : undefined
                          }
                        >
                          <Header
                            variant={'h3'}
                            data-unlinked={item.id === unlinkedId}
                          >
                            <span className={'text-base'}>{item.title}</span>
                          </Header>
                        </Link>
                      </div>
                      {/* What drilling into this card yields. Absent on a card
                          with no children, so a dead end reads as one. The count
                          sits a size step below the title rather than a shade
                          lighter — atomic-ui's `muted` variant is #A2A2B3, which
                          is 2.5:1 on white and fails as text. */}
                      {item.childCount !== null && item.childCount > 0 && (
                        <DrillCount
                          count={item.childCount}
                          label={t('dashboard.child_count', {
                            count: item.childCount,
                          })}
                        />
                      )}
                    </div>
                  ),
                },
              ],
              header: (item) =>
                item.id === unlinkedId ? null : (
                  /* Wrapping, not one fixed row. `SimpleRatingBadge` carries a
                     `min-width: 80px` and clips its own label, so on a narrow card it
                     cannot shrink to share a row with the friendly id — it overflows
                     and the card clips it to "Unratec". Given a line of its own it
                     renders whole at every width. */
                  <div
                    className={'flex flex-wrap items-center gap-x-2 gap-y-1'}
                  >
                    {item.id !== unlinkedId && (
                      <div
                        className={
                          'text-grey text-sm flex-grow whitespace-nowrap'
                        }
                      >
                        {getFriendlyId(
                          Parent_Type_Enum.Obligation,
                          item.SequentialId
                        )}
                      </div>
                    )}
                    {item.regulatorLabel && (
                      <Badge variant={'primary'}>{item.regulatorLabel}</Badge>
                    )}
                    {item.rating !== null && (
                      <SimpleRatingBadge rating={item.rating} />
                    )}
                  </div>
                ),
            }}
            cardsPerRow={[{ cards: 1 }]}
            items={cardItems}
            empty={empty}
            loading={loading}
            loadingText={t('loading_message')}
            visibleSections={['title']}
            selectionType={'single'}
            selectedItems={selectedItems}
            onSelectionChange={onSelectionChange}
            trackBy={(item) => item.id ?? item.title}
          />
        </SpaceBetween>
      </Container>
    </div>
  );
};

export default Type;
